import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { createDirectDebitPromotion } from '../../services/requests.service';
import { showAxiosMessageError } from '../../services/error-message.service';
import DirectDebitFormPromotion from '../../components/forms/direct-debit/direct-debit-promotion.form';
import { DirectDebitFormPromotionDto } from '../../services/dtos/form/direct-debit-form-promotion.dto';
import { DirectDebitPromotionDto } from '../../services/dtos/direct-debit-promotion.dto';

/**
 * Page qui permet d'ajouter une prélémvement automatique
 * @constructor
 */
export default function PromotionMensualisationDirectDebitPage(): JSX.Element {
    //Params
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    const [sendForm, setSendForm] = useState(true);
    const [file, setFile] = useState<string>('');
    const [contentType, setContentType] = useState<string>('');
    const [fileName, setFileName] = useState<string>('');
    const [success, setSuccess] = useState(false);
    const [codeAcces, setCodeAcces] = useState<string>('');
    const [messageType, setMessageType] = useState<string>('');

    const location = useLocation();

    const [searchParams] = useSearchParams();

    const token = searchParams.get('token');

    const isMensualise = location.state?.mensualise ?? false;

    //Navigate
    const navigate = useNavigate();

    useEffect(() => {
        const code = localStorage.getItem('codeAcces');
        if (!token || !code) {
            navigate('/', { replace: true });
        }

        if (code !== null) {
            const tmpMessageType = localStorage.getItem('messageType') ?? '5';
            setMessageType(tmpMessageType);
            setCodeAcces(code);
        }
    }, []);

    //handler sur l'envoi du formulaire
    const handleSumbitForm = (form: DirectDebitFormPromotionDto) => {
        setSendForm(false);
        const seven = 7;
        const data: DirectDebitPromotionDto = {
            titulaire: form.holder,
            bic: form.bic,
            iban: form.iban,
            membres: form.members,
            rib: file.substring(file.indexOf('base64,') + seven),
            email: form.email,
            tel: `0${form.tel}`,
            codeAcces: codeAcces,
            message: messageType,
        };

        createDirectDebitPromotion(token, data)
            .then(() => {
                setSuccess(true);
                localStorage.removeItem('codeAcces');
                localStorage.removeItem('messageType');
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setSendForm(true);
            });
    };

    //handler sur le changement de valeur du input file
    const handleChangeFile = (event: any) => {
        const targetFile = event.target.files[0];
        setFileName(targetFile.name);
        setContentType(targetFile.type);
        convertToBase64(targetFile)
            .then((value: any) => {
                setFile(value);
            })
            .catch(() => {
                showMessage.errorAlert('Erreur lors du traitement du fichier.');
            });
    };

    /**
     * Méthode qui converti le fichier en base64
     * @param targetFile fichier
     */
    const convertToBase64 = (targetFile: any) => {
        return new Promise((resolve, reject) => {
            //Récupération du contenu de l'image
            const fileReader = new FileReader();
            fileReader.readAsDataURL(targetFile);

            //Définition dans le success
            fileReader.onload = () => {
                resolve(fileReader.result);
            };

            //Définition dans l'error
            fileReader.onerror = error => {
                reject(error);
            };
        });
    };

    if (success) {
        return (
            <Container maxWidth="xl" sx={{ mt: { xs: 4, md: 15 } }}>
                <Paper sx={{ p: 4, borderRadius: 4, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h5" fontWeight={800} color="primary.dark">
                            Succès
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Vous allez recevoir d'ici quelques minutes un e-mail contenant un lien sécurisé pour
                            procéder à la <strong>signature électronique</strong> de votre contrat de mensualisation.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Pensez à vérifier vos courriers indésirables (spams) si le message ne s'affiche pas dans
                            votre boîte de réception d'ici peu.
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Button
                            onClick={() => {
                                navigate('/');
                            }}
                            variant="contained"
                        >
                            Retour à l'accueil
                        </Button>
                    </Box>
                </Paper>
            </Container>
        );
    }

    return (
        <DirectDebitFormPromotion
            handleSumbitForm={handleSumbitForm}
            handleChangeFile={handleChangeFile}
            sendForm={sendForm}
            contentType={contentType}
            fileName={fileName}
            isMensualise={isMensualise}
        />
    );
}
